/**
 * Der lokale Speicher der Warteschlange.
 *
 * Die Warteschlange spricht nur mit diesen wenigen Funktionen, nicht mit
 * Dexie selbst. Die Datenbank dahinter haelt seit Stufe 3 auch die lokale
 * Kopie des Bestands (`lokal/datenbank.ts`); was darueber liegt, merkt davon
 * nichts.
 */

import type { Buchung } from "@thw/contracts";
import { db, type WartendeBuchung } from "../lokal/datenbank.js";

export type { WartendeBuchung } from "../lokal/datenbank.js";

export async function ablegen(eintrag: WartendeBuchung): Promise<void> {
  await db.buchungen.put(eintrag);
}

export function alleMit(zustand: WartendeBuchung["zustand"]): Promise<WartendeBuchung[]> {
  return db.buchungen.where("zustand").equals(zustand).sortBy("angelegt_am");
}

export function zaehlenMit(zustand: WartendeBuchung["zustand"]): Promise<number> {
  return db.buchungen.where("zustand").equals(zustand).count();
}

/** Mehrere Eintraege in **einer** Transaktion aendern. */
export async function stapelSchreiben(eintraege: WartendeBuchung[]): Promise<void> {
  await db.buchungen.bulkPut(eintraege);
}

/**
 * Angekommene Buchungen aus der Warteschlange ins lokale Log umziehen.
 *
 * In **einer** Transaktion: Laege eine Buchung einen Augenblick weder in der
 * Warteschlange noch im Log, zeigte die Oberflaeche fuer diesen Augenblick den
 * alten Zustand -- und im Funkloch, wenn der Abruf des Logs danach scheitert,
 * auf Dauer.
 *
 * Jede wird zugleich zur Pruefung vorgemerkt: War sie beim Eintreffen schon
 * von einer neueren ueberstimmt (KONZEPT.md §15.4)? Das laesst sich erst
 * sagen, wenn das Log bis zu ihrer Folgenummer vorliegt.
 */
export async function angekommen(buchungen: Buchung[]): Promise<void> {
  await db.transaction("rw", [db.buchungen, db.log, db.pruefen], async () => {
    const ids = buchungen.map((b) => b.id);
    await db.buchungen.bulkDelete(ids);
    // Was der Abruf des Logs schon gebracht hat, ist genauer als diese
    // Abschrift -- dort steht der echte Eingangszeitpunkt.
    const bekannt = new Set((await db.log.bulkGet(ids)).map((b) => b?.id));
    await db.log.bulkPut(buchungen.filter((b) => !bekannt.has(b.id)));
    await db.pruefen.bulkPut(buchungen.map((b) => ({ id: b.id, folge: b.folge })));
  });
}
